import {getMonth, getYear} from 'date-fns';
import {Flag, Tooltip} from 'platform/components';
import {Box, HStack, Icon, Show, Text, VStack} from 'platform/foundation';
import {useDateTimeFormatter, useFormatCurrency, useFormatNumber} from 'platform/locale';
import {match} from 'ts-pattern';

import {isNotNil} from 'ramda-adjunct';

import {SourcingVehicleResponseBody} from '@dms/api/core';
import i18n from '@dms/i18n';

import {Nullish, suffixTestId, TestIdProps} from 'shared/web';

import {NL_BPM_ESTIMATE_CURRENCY, NL_BPM_TARIFF_YEAR} from '../constants/nlBpmTariff';
import {calculateNlBpm, NlBpmUnavailableReason} from '../utils/calculateNlBpm';
import {getManufactureYear} from '../utils/getManufactureYear';
import {BpmBreakdown} from './BpmBreakdown';
import {BpmScenarios} from './BpmScenarios';
import {BpmUnavailable} from './BpmUnavailable';

interface BpmEstimateCardProps extends TestIdProps {
  sourcingVehicle: SourcingVehicleResponseBody;
}

const getUnavailableMessage = (reason: NlBpmUnavailableReason) =>
  match(reason)
    .with('NO_CO2', () => i18n.t('entity.sourcing.bpm.unavailable.noCo2'))
    .with('NO_REGISTRATION_DATE', () =>
      i18n.t('entity.sourcing.bpm.unavailable.noRegistrationDate')
    )
    .with('VAN', () => i18n.t('entity.sourcing.bpm.unavailable.van'))
    .exhaustive();

const getEmissionSource = (origin: 'ad' | 'adac_emissions' | Nullish) =>
  match(origin)
    .with('ad', () => i18n.t('entity.sourcing.bpm.emissionSource.ad'))
    .with('adac_emissions', () => i18n.t('entity.sourcing.bpm.emissionSource.catalogue'))
    .otherwise(() => null);

/**
 * BPM is always in euros, the workspace currency is not, and there is no rate
 * to convert with here. The advertised price in the original currency is the
 * one figure that is the same for every user, so the total is built from that
 * and from nothing else - a total that changed with the viewer's currency
 * settings would be worse than no total at all.
 */
const getAdPriceInEur = (vehicle: SourcingVehicleResponseBody) =>
  vehicle.price?.originalCurrency?.key === NL_BPM_ESTIMATE_CURRENCY
    ? vehicle.price.originalWithVat
    : null;

export function BpmEstimateCard(props: BpmEstimateCardProps) {
  const formatCurrency = useFormatCurrency();
  const formatNumber = useFormatNumber();
  const formatDateTime = useDateTimeFormatter();

  const vehicle = props.sourcingVehicle;
  const today = new Date();

  const estimate = calculateNlBpm({
    co2: vehicle.carbonDioxideEmission,
    registrationDate: vehicle.registrationDate,
    manufactureYear: getManufactureYear(vehicle),
    fuelTypeKey: vehicle.fuelType?.key,
    carStyleKey: vehicle.carStyle?.key,
    mileage: vehicle.mileage,
    today,
  });

  // useFormatCurrency has a nullable signature; these are always finite numbers.
  const formatAmount = (amount: number) =>
    formatCurrency(amount, NL_BPM_ESTIMATE_CURRENCY, 0) ?? '';

  // The emission cycle warning belongs to both computed states, not just the single estimate.
  const isEmissionCycleUncertain =
    estimate.status !== 'unavailable' && estimate.isEmissionCycleUncertain;

  // The rates live in the bundle, so the card has to flag their age itself.
  const isTariffStale = estimate.status !== 'unavailable' && estimate.isTariffStale;

  const registrationBasis = estimate.status !== 'unavailable' ? estimate.registrationBasis : null;

  // Without the first registration the age is counted from 31 December of the
  // manufacture year - a pessimistic, indicative figure, and the card has to say so.
  const assumedRegistrationMessage =
    registrationBasis?.source === 'manufactureYear'
      ? i18n.t('entity.sourcing.bpm.assumedRegistration.message', {
          year: registrationBasis.manufactureYear,
        })
      : null;

  const isLowConfidence =
    isEmissionCycleUncertain || isTariffStale || isNotNil(assumedRegistrationMessage);

  const emissionSource = getEmissionSource(vehicle.carbonDioxideEmissionOrigin);

  const adPrice = getAdPriceInEur(vehicle);

  // Sources publish the first registration with month precision, so that is how
  // it is shown; a full date would pretend to a precision we do not have.
  const registrationLabel = match(registrationBasis)
    .with(
      {source: 'firstRegistration'},
      (basis) =>
        `${i18n.t('entity.vehicle.labels.firstRegistration')} ${String(
          getMonth(basis.date) + 1
        ).padStart(2, '0')}/${getYear(basis.date)}`
    )
    .with({source: 'manufactureYear'}, (basis) =>
      i18n.t('entity.sourcing.bpm.manufactureYear', {year: basis.manufactureYear})
    )
    .with(null, () => null)
    .exhaustive();

  const asOfLabel = i18n.t('entity.sourcing.bpm.estimateAsOf', {
    date: formatDateTime('dateShort', today),
  });

  const getFootnote = (isElectric: boolean) =>
    [
      isElectric
        ? i18n.t('entity.sourcing.bpm.electricNote')
        : `${i18n.t('entity.sourcing.bpm.basedOn')} ${formatNumber(
            vehicle.carbonDioxideEmission,
            0
          )} ${i18n.t('general.metric.gkm')}${emissionSource ? ` (${emissionSource})` : ''}`,
      registrationLabel,
    ]
      .filter(isNotNil)
      .join(' · ');

  // A neutral "Estimate" flag disappears on the grey background and adds nothing,
  // since the date sits right under the amount. Only meaningful flags remain.
  const getFlag = () => {
    if (estimate.status === 'unavailable') {
      return null;
    }

    if (isLowConfidence) {
      return {
        label: i18n.t('entity.sourcing.bpm.flag.lowConfidence'),
        colorScheme: 'orange' as const,
      };
    }

    if (estimate.status === 'twoScenarios') {
      return {
        label: i18n.t('entity.sourcing.bpm.flag.twoScenarios'),
        colorScheme: 'blue' as const,
      };
    }

    return null;
  };

  const flag = getFlag();

  return (
    <Box
      backgroundColor="palettes.neutral.20.100"
      borderRadius="medium"
      padding={4}
      data-testid={suffixTestId('bpmEstimate', props)}
    >
      <VStack spacing={3}>
        <HStack justify="space-between" align="center" spacing={2}>
          <HStack align="center" spacing={1}>
            <Text size="xSmall" color="secondary">
              {i18n.t('entity.sourcing.bpm.title')}
            </Text>
            <Tooltip
              placement="top"
              label={i18n.t('entity.sourcing.bpm.tooltip', {year: NL_BPM_TARIFF_YEAR})}
            >
              <Icon value="action/info_outline" size={4} color="text.secondary" />
            </Tooltip>
          </HStack>
          <Show when={isNotNil(flag)}>
            <Flag label={flag?.label ?? ''} colorScheme={flag?.colorScheme} size="small" isSubtle />
          </Show>
        </HStack>

        <Show when={isTariffStale}>
          <BpmUnavailable
            variant="warning"
            title={i18n.t('entity.sourcing.bpm.staleTariff.title')}
            message={i18n.t('entity.sourcing.bpm.staleTariff.message', {
              tariffYear: NL_BPM_TARIFF_YEAR,
              currentYear: today.getFullYear(),
            })}
            data-testid={suffixTestId('staleTariff', props)}
          />
        </Show>

        <Show when={assumedRegistrationMessage}>
          {(message) => (
            <BpmUnavailable
              variant="warning"
              title={i18n.t('entity.sourcing.bpm.assumedRegistration.title')}
              message={message}
              data-testid={suffixTestId('assumedRegistration', props)}
            />
          )}
        </Show>

        <Show when={isEmissionCycleUncertain}>
          <BpmUnavailable
            variant="warning"
            title={i18n.t('entity.sourcing.bpm.cycleUnknown.title')}
            message={i18n.t('entity.sourcing.bpm.cycleUnknown.message')}
            data-testid={suffixTestId('cycleUnknown', props)}
          />
        </Show>

        {match(estimate)
          .with({status: 'unavailable'}, (unavailable) => (
            <BpmUnavailable
              title={i18n.t('entity.sourcing.bpm.unavailable.title')}
              message={getUnavailableMessage(unavailable.reason)}
              data-testid={suffixTestId('bpmUnavailable', props)}
            />
          ))
          .with({status: 'twoScenarios'}, (scenarios) => (
            <BpmScenarios
              key={vehicle.adId}
              scenarios={scenarios}
              isFixedRate={scenarios.isElectric}
              adPrice={adPrice}
              subtitle={asOfLabel}
              footnote={getFootnote(scenarios.isElectric)}
              data-testid={suffixTestId('scenarios', props)}
            />
          ))
          .with({status: 'computed'}, (computed) => (
            <BpmBreakdown
              estimate={computed.estimate}
              grossBpm={computed.breakdown.grossBpm}
              depreciationPercent={computed.breakdown.depreciationPercent}
              isFixedRate={computed.isElectric}
              adPrice={adPrice}
              subtitle={[
                asOfLabel,
                isNotNil(computed.rangeMin) && isNotNil(computed.rangeMax)
                  ? i18n.t('entity.sourcing.bpm.range', {
                      min: formatAmount(computed.rangeMin),
                      max: formatAmount(computed.rangeMax),
                    })
                  : null,
              ]
                .filter(isNotNil)
                .join(' · ')}
              footnote={getFootnote(computed.isElectric)}
              data-testid={suffixTestId('breakdown', props)}
            />
          ))
          .exhaustive()}

        <Show when={estimate.status !== 'unavailable'}>
          <Text size="xxSmall" color="tertiary">
            {i18n.t('entity.sourcing.bpm.disclaimer', {year: NL_BPM_TARIFF_YEAR})}
          </Text>
        </Show>
      </VStack>
    </Box>
  );
}
